/**
 * The tower — 4096 parcels on sixteen floors of different sizes, as one WebGL scene.
 *
 * **The silhouette is the census.** Terraforms' Hypercastle is not a box: measured from
 * EnterDream's own `3d/data.js`, its twenty levels have footprints
 * `[4,8,8,16,16,24,24,24,16,32,32,16,48,48,24,24,16,8,8,4]` — a narrow foot, stepped slabs, two
 * necks, a wide canopy near the top, a spire — and the squares of those sum to its `maxSupply`
 * exactly. So the shape is not styling laid over a uniform stack; the shape is *how many parcels
 * each floor holds*. `art/placement.ts` carries our sixteen-level version of that census and
 * `manifest.json` ships it as `sides`, so this file draws the tower the data describes rather
 * than a shape of its own invention. Floor 1 is 4x4 and 16 parcels; floor 11 is 32x32 and 1024.
 *
 * **The stills are what opens.** A visitor lands on the sixteen 2048px atlases `tools/atlas.ts`
 * bakes: the same 4096 parcels at frame 0, sixteen draw calls, one texture fetch and a multiply per
 * fragment. It is the cheap half on first contact and it is fully drawn as soon as the last page
 * arrives, which is what a visitor who came to look at a tower wants first.
 *
 * **The live parcels are one keystroke away.** `A`, or the toggle in the HUD, streams the five
 * textures `tools/towerdata.ts` bakes and hands the whole collection to `src/parcels.ts`: one
 * `InstancedMesh` over all 4096 ids running the engine's own GLSL from `emit/glsl.ts`, every parcel
 * on its own twelve-frame glitch loop off one shared clock. That is the artwork itself rather than a
 * picture of it. What makes it affordable is the LOD ladder in the shader rather than anything here:
 * a parcel a few pixels tall costs one texel fetch of a baked block mean instead of 1024 cells of
 * gather, and the crossover is a Nyquist limit the `detail` slider can move. `shadercheck.ts` holds
 * that shader to `applyFrame`, to the engine's schedule, to three's mip chain and to resvg for all
 * 4096 tokens before this file ever runs.
 *
 * **Two renderers of one census, a keystroke apart**, is the fastest way to see whether the live
 * shader is drawing the art or something that merely looks like it. Both write raw sRGB bytes: no
 * `colorSpace` on either texture, because a tagged texture is decoded to linear on sample and
 * written out as if it were sRGB, which is exactly how the stills used to render dark.
 *
 * **Sixteen draw calls, not 4096.** For the stills, the unit of instancing is an *atlas page* of
 * 256 ids, not a
 * floor — floors now run from 16 to 1024 parcels, so a mesh per floor would be sixteen meshes of
 * wildly different size and sixteen textures of wildly different waste. Each page is one
 * `InstancedMesh` of 256 unit quads sharing one 2048px atlas, and every instance carries the tile
 * it samples and the floor it stands on. A page straddles floors near the narrow ends, which is
 * why the floor is an attribute rather than a property of the mesh.
 *
 * **Hiding a floor is a shader decision, either way.** With a mesh per floor, the floor sliders
 * were `mesh.visible`. A page is not a floor, so `uShown`/`uSolo` go to the page vertex shader and
 * a hidden instance is collapsed to `vec4(2, 2, 2, 1)` — outside the clip volume, so it is
 * discarded before rasterising. The live parcels reach the same place from the other end: their
 * material has no idea what a floor is, and takes visibility and brightness per instance through
 * `present`, so the floor sliders, the highlight and the depth ramp are arithmetic here on the CPU
 * and two 16 KB uploads there. The policy belongs to the tower; all the renderer promises is that
 * a hidden parcel costs nothing.
 *
 * **The atlas layout is not a lookup table.** `tools/atlas.ts` writes id `i` at tile
 * `(i>>4 & 15, i & 15)` of page `i>>8`, so the UV offset is arithmetic on the id and nothing here
 * has to be told where a token is. The one wrinkle is that image space runs downward and texture
 * space runs upward, which is the `1 - (y + 1) / grid` below and the only place a sign can go
 * wrong.
 *
 * **Floors are centred, not corner-aligned.** The reference contract offsets each level by
 * `(48 - side) / 2`; checked against its data, every level shares one structure-space centre. So
 * a parcel's world offset is `x - (side - 1) / 2` and the stack has a single vertical axis, which
 * is what makes the necks read as necks instead of as a lean.
 *
 * **The floor pitch is measured, not chosen.** In the reference, consecutive levels sit 224
 * structure units apart and a parcel is 32 across — a 7:1 ratio. The `floor spacing` slider
 * defaults to 7.4 against a parcel gap of 1.06, which is that ratio.
 *
 * **Neither half is fetched before it is wanted.** Sixteen atlases is tens of megabytes, so a page
 * is added to the scene as its texture arrives — ids ascend with height, so page 0 is the ground and
 * page 15 the spire, and the tower grows upward while you are already looking at it. The other
 * direction is the same promise kept the other way round: the 8.7 MB bake is not asked for until the
 * live toggle is, and then it is asked for once. `data.json` is the exception, at 29 KB — it is the
 * bake's descriptor, and it is what lets the status bar name the collection's salt and the check
 * cross-examine the atlas against the bake on a page that has not loaded either.
 *
 * **The level axis is DOM.** Each floor's number is an absolutely positioned element whose
 * `translateY` comes from `Vector3.project` every frame. Sprites in the scene would need a font
 * atlas and would still shrink with distance; a ruler should not.
 */

import {
  BufferAttribute,
  BufferGeometry,
  ClampToEdgeWrapping,
  DataTexture,
  DoubleSide,
  Group,
  InstancedBufferAttribute,
  InstancedMesh,
  LinearFilter,
  LineBasicMaterial,
  LineSegments,
  Matrix4,
  NearestFilter,
  Object3D,
  PerspectiveCamera,
  PlaneGeometry,
  Raycaster,
  RGBAFormat,
  Scene,
  ShaderMaterial,
  SRGBColorSpace,
  Texture,
  TextureLoader,
  Vector2,
  Vector3,
  WebGLRenderer,
} from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

import { Parcels, assertTextureLimits, fetchTowerData, loadTowerTextures } from "./parcels.js";

/** The columnar manifest `tools/atlas.ts` writes. */
interface Manifest {
  readonly salt: string;
  readonly supply: number;
  readonly levels: number;
  /** The census: parcels along each axis of each floor, ground first. */
  readonly sides: readonly number[];
  readonly span: number;
  readonly pages: number;
  readonly parcelsPerPage: number;
  readonly tileGrid: number;
  readonly tile: number;
  readonly atlasPx: number;
  /**
   * The two-tier atlas fields, optional because a manifest can predate them.
   *
   * `index.html` is served `no-cache` while `manifest.json` is cached for an hour, so for up to an
   * hour after a deploy a returning visitor can run this module against the previous bake's manifest.
   * That pairing has to degrade rather than break: no `atlasFormat` means the old `.png` pages, no
   * byte tables mean the loading bar counts files instead of bytes, and `previewPx: 0` means there is
   * no small tier to stream first. Every read of these five goes through `??`.
   */
  readonly atlasFormat?: string;
  /** Bytes of each full page and each preview page, in page order: the loading bar's denominator. */
  readonly pageBytes?: readonly number[];
  readonly previewPx?: number;
  readonly previewTile?: number;
  readonly previewBytes?: readonly number[];
  readonly names: Record<string, readonly string[]>;
  readonly columns: {
    readonly archetype: readonly number[];
    readonly palette: readonly number[];
    readonly glyphSet: readonly number[];
    readonly biome: readonly number[];
    readonly glitch: readonly number[];
    readonly tint: readonly number[];
    readonly hot: readonly number[];
    readonly bg: readonly string[];
  };
}
/** The five columns worth filtering on: the named traits, not the numeric ones. */
const FILTER_COLUMNS = ["archetype", "palette", "glyphSet", "biome", "glitch"] as const;
type FilterColumn = (typeof FILTER_COLUMNS)[number];

const el = <T extends HTMLElement>(id: string): T => {
  const node = document.getElementById(id);
  if (node === null) throw new Error(`missing #${id}`);
  return node as T;
};

const statusBar = el<HTMLDivElement>("status");
const statusText = el<HTMLSpanElement>("statusText");
const fpsOut = el<HTMLSpanElement>("fps");
const canvas = el<HTMLCanvasElement>("view");

// The loading panel, which `index.html` ships visible so that it is on screen before this module has
// finished parsing. Everything it says from here on is a real byte count out of the atlas manifest.
const boot = el<HTMLDivElement>("boot");
const bootSub = el<HTMLParagraphElement>("bootSub");
const bootFill = el<HTMLElement>("bootFill");
const bootPages = el<HTMLDivElement>("bootPages");
const bootPct = el<HTMLSpanElement>("bootPct");
const bootBytes = el<HTMLSpanElement>("bootBytes");

/**
 * Put a boot failure where the visitor is already looking.
 *
 * The two fetches below are top-level awaits: if either rejects, the module never reaches the line
 * that would have reported it, and a missing `manifest.json` becomes a loading panel that says
 * "reading the bake manifest" forever. `bootFail` rethrows on top of that — the console still gets
 * the stack — and `bootSay` is the same message without the throw, for the net below.
 */
function bootSay(what: string, err: unknown): void {
  // First reason wins. `bootFail` names the thing that actually broke and *then* rethrows, so the net
  // below sees that same failure a second time as a bare window error and would otherwise replace a
  // sentence a visitor can act on ("no WebGL context — hardware acceleration may be switched off") with
  // the generic one. The specific line is the whole point of painting anything here.
  if (boot.classList.contains("bad")) return;
  boot.classList.add("bad");
  bootSub.textContent = `${what} — ${err instanceof Error ? err.message : String(err)}`;
}

function bootFail(what: string, err: unknown): never {
  bootSay(what, err);
  throw err;
}

/** The panel's leaving animation, and then its removal from the layout and the a11y tree. */
function bootDone(): void {
  if (boot.hidden || boot.classList.contains("gone")) return;
  boot.classList.add("gone");
  setTimeout(() => {
    boot.hidden = true;
  }, 460);
}

/**
 * Anything this module throws, in front of the visitor instead of in a console they will not open.
 *
 * Everything from here to the first drawn frame runs with the loading panel as the only thing on
 * screen, and most of it can fail on a machine this repo has never run on: a browser that refuses a
 * WebGL context, a driver that will not link the shader, a manifest from a different bake. Every one of
 * those used to leave the panel sitting on its opening line for as long as the tab stayed open, which
 * is indistinguishable from a slow connection and says nothing a visitor can act on.
 *
 * Only script errors are taken. A resource that failed has an element for a target and its own
 * reporting — `atlasFailed` names the page and the file — and treating a stray 404 as a fatal start
 * would put the panel up for good over a tower that is fine. The one failure this cannot catch is
 * `main.js` never parsing, since the listener would be inside it; that is what running the real bundle
 * in a real browser on every `pnpm tower:check` is for.
 */
addEventListener(
  "error",
  (ev: ErrorEvent) => {
    if (ev.target !== null && ev.target !== window) return;
    bootSay("the page could not start", (ev.error as unknown) ?? ev.message);
  },
  true,
);
addEventListener("unhandledrejection", (ev: PromiseRejectionEvent) => {
  bootSay("the page could not start", ev.reason);
});

/**
 * Two deadlines, because a panel that never leaves is the worst of the three things it can be.
 *
 * The panel earns its place by covering the seconds where there is nothing to look at, and it leaves
 * when the preview tier completes — the tower whole and legible. On a slow line that is several
 * seconds, and on a line slow enough it is more than anyone will wait staring at one sentence. So it
 * gets a ceiling: at `BOOT_CEILING_MS`, if any page at all has landed, the panel goes and the status
 * bar carries the rest of the count over a tower that is already growing floor by floor.
 *
 * If *nothing* has landed there is nothing behind the panel to reveal, so instead of leaving it says
 * which of the two things it is waiting for — the bake's descriptors, or the first atlas page — and
 * gives it until `BOOT_STALL_MS` before handing that same sentence to the status bar and going anyway.
 * Both deadlines stand down if the panel has already reported a failure: a page that cannot start says
 * so and keeps saying so.
 *
 * The state both timers read is the strip of sixteen cells the visitor is looking at, not a counter of
 * their own — and nothing they touch is declared later in this file, because the stall these exist to
 * catch is the module still being parked on a top-level await when they fire.
 */
const BOOT_CEILING_MS = 6_000;
const BOOT_STALL_MS = 14_000;
const bootGone = (): boolean => boot.hidden || boot.classList.contains("gone");
const bootIdle = (): boolean => !bootGone() && !boot.classList.contains("bad");
const bootLanded = (): number => bootPages.querySelectorAll("i.lo, i.hi").length;
const bootWaitingOn = (): string =>
  bootPages.children.length === 0
    ? "still reading the bake — no atlas page has been asked for yet"
    : `still waiting on the atlas — ${bootLanded()} of ${bootPages.children.length} pages in`;
setTimeout(() => {
  if (!bootIdle()) return;
  if (bootLanded() > 0) {
    bootDone();
    return;
  }
  bootSub.textContent = bootWaitingOn();
  setTimeout(() => {
    if (!bootIdle()) return;
    statusText.textContent = bootWaitingOn();
    statusBar.style.opacity = "1";
    bootDone();
  }, BOOT_STALL_MS - BOOT_CEILING_MS);
}, BOOT_CEILING_MS);

/** The port `tools/serve.ts` listens on, so a click can open the real animated token. */
const PREVIEW_PORT = 5199;

const manifest: Manifest = await fetch("./manifest.json")
  .then((r) => {
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json() as Promise<Manifest>;
  })
  .catch((err: unknown) => bootFail("manifest.json", err));

/**
 * The animated half's descriptor: `data.json`, as written by `tools/towerdata.ts`.
 *
 * `fetchTowerData` validates it against the shader's own constants — frame count, loop length, cell
 * grid, endianness, every texture's format and byte length — so a stale or mismatched bake stops
 * here with a sentence rather than drawing something subtly wrong. What is left to check is that
 * the bake and the atlas are the *same collection*, which is the salt and the supply: two bakes of
 * different salts would put one token's art under another token's traits, and nothing downstream
 * would notice.
 */
const towerData = await fetchTowerData(".").catch((err: unknown) => bootFail("data.json", err));
if (towerData.supply !== manifest.supply) {
  bootFail(
    "the bake and the atlas are different collections",
    new Error(`the bake holds ${towerData.supply} tokens, the atlas manifest ${manifest.supply}`),
  );
}
if (towerData.salt !== manifest.salt) {
  bootFail(
    "the bake and the atlas are different collections",
    new Error(`the bake's salt is ${towerData.salt}, the atlas's ${manifest.salt}`),
  );
}

const SUPPLY = manifest.supply;
const LEVELS = manifest.levels;
/** The widest floor, which every narrower floor is centred inside. */
const SPAN = manifest.span;
const PAGES = manifest.pages;
const PER_PAGE = manifest.parcelsPerPage;
const TILE_GRID = manifest.tileGrid;
const SIDES = manifest.sides;

/**
 * The census as lookup tables, built once.
 *
 * `art/placement.ts` walks a prefix table to answer "which floor is this id on"; here that answer
 * is wanted per frame for the ruler, per parcel for the layout, and per hit for picking, so it is
 * paid for once as a byte per token — four kilobytes for the whole collection.
 *
 * `OFF_X`/`OFF_Y` are the other half: a parcel's offset from its floor's centre in parcel units,
 * `x - (side - 1) / 2`, which is the centring the reference contract does with `(48 - side) / 2`.
 * Multiplying by the parcel gap is all that stands between these and world space, so the layout
 * and the marker and the fly-to all agree by construction rather than by keeping two formulas in
 * step.
 */
const LEVEL_OF = new Uint8Array(SUPPLY);
const OFF_X = new Float32Array(SUPPLY);
const OFF_Y = new Float32Array(SUPPLY);
const LEVEL_BASE = new Int32Array(LEVELS);
{
  let id = 0;
  for (let level = 0; level < LEVELS; level++) {
    const side = SIDES[level] ?? 0;
    LEVEL_BASE[level] = id;
    const centre = (side - 1) / 2;
    for (let x = 0; x < side; x++) {
      for (let y = 0; y < side; y++) {
        LEVEL_OF[id] = level;
        OFF_X[id] = x - centre;
        OFF_Y[id] = y - centre;
        id++;
      }
    }
  }
  if (id !== SUPPLY) throw new Error(`the census holds ${id} parcels, the manifest says ${SUPPLY}`);
}

const tokenLevel = (id: number): number => LEVEL_OF[id] ?? 0;
const levelSide = (level: number): number => SIDES[level] ?? 0;
/** Column and row on the token's own floor, which is `levelSide(level)` wide, not `SPAN`. */
const tokenX = (id: number): number => Math.floor((id - (LEVEL_BASE[tokenLevel(id)] ?? 0)) / levelSide(tokenLevel(id)));
const tokenY = (id: number): number => (id - (LEVEL_BASE[tokenLevel(id)] ?? 0)) % levelSide(tokenLevel(id));

// ---------------------------------------------------------------- scene

/**
 * The grey-to-black backdrop, as a 1x64 texture three stretches over the frame.
 *
 * In the scene rather than a CSS gradient behind a transparent canvas, because the check reads
 * the drawing buffer: a CSS backdrop is invisible to `readPixels`, so what gets verified would
 * not be what a visitor sees. `DataTexture` leaves `flipY` false, so row 0 is the bottom.
 */
function backdrop(): DataTexture {
  const rows = 64;
  const data = new Uint8Array(rows * 4);
  for (let i = 0; i < rows; i++) {
    const t = i / (rows - 1);
    // Squared rather than linear: a straight ramp puts mid grey across the middle of the frame,
    // which is exactly where the parcels are and what they have to stay legible against.
    const v = Math.round(5 + 48 * t * t);
    data[i * 4] = v;
    data[i * 4 + 1] = v;
    data[i * 4 + 2] = v + 7;
    data[i * 4 + 3] = 255;
  }
  const tex = new DataTexture(data, 1, rows, RGBAFormat);
  tex.colorSpace = SRGBColorSpace;
  tex.minFilter = LinearFilter;
  tex.magFilter = LinearFilter;
  tex.wrapS = ClampToEdgeWrapping;
  tex.wrapT = ClampToEdgeWrapping;
  tex.needsUpdate = true;
  return tex;
}
/**
 * The one line on this page that a perfectly healthy browser can refuse.
 *
 * `WebGLRenderer` throws when the browser hands back no context at all — hardware acceleration switched
 * off, a driver on the vendor blocklist, a machine with GPU access denied to the sandbox. There is no
 * fallback to give: this page is a shader. What there is instead is a sentence in the panel the visitor
 * is already reading, rather than a page that loads forever with the reason in a console.
 */
let renderer: WebGLRenderer;
try {
  renderer = new WebGLRenderer({ canvas, antialias: true });
} catch (err: unknown) {
  bootFail("this browser gave the page no WebGL context — hardware acceleration may be switched off", err);
}
renderer.outputColorSpace = SRGBColorSpace;

const scene = new Scene();
scene.background = backdrop();

const camera = new PerspectiveCamera(46, 1, 0.1, 4000);
const controls = new OrbitControls(camera, renderer.domElement);
controls.enableDamping = true;
controls.dampingFactor = 0.075;
controls.maxDistance = 900;
controls.minDistance = 2;

/** Floors sit `gap` apart and parcels `pitch` apart; 7.4 to 1.06 is the reference's 7:1. */
let gap = 7.4;
let pitch = 1.06;
let shown = LEVELS;
let solo = 0;
let selected = -1;

/**
 * Frame the whole tower. Also the `R` key.
 *
 * The distance is solved rather than guessed: the vertical need is the full height of the stack
 * and the horizontal need is the widest floor across its diagonal, since this view looks at a
 * corner. A hand-picked position looked right at one spacing and cropped the bottom floors the
 * moment it changed — and the level axis notices, because a tick whose floor projects off screen
 * hides itself.
 */
function resetView(): void {
  const h = (LEVELS - 1) * gap;
  const w = SPAN * pitch;
  const aim = new Vector3(0, h / 2, 0);
  const half = Math.tan((camera.fov * Math.PI) / 360);
  const dist =
    Math.max(h / 2 / half, (w * Math.SQRT1_2) / (half * Math.max(0.35, camera.aspect))) * 1.14 + SPAN * 0.25;
  camera.position.copy(aim).addScaledVector(new Vector3(0.58, 0.42, 0.7).normalize(), dist);
  controls.target.copy(aim);
  controls.update();
}

// A unit quad lying flat in XZ, facing up. After this rotation the quad's own +Y — the
// direction the artwork's top edge points — becomes world -Z, so a camera on +Z reads every
// parcel upright without any per-instance billboarding.
const quad = new PlaneGeometry(1, 1);
quad.rotateX(-Math.PI / 2);

const towerGroup = new Group();
scene.add(towerGroup);

// ---------------------------------------------------------------- the live parcels

/**
 * The animated tower: 8.7 MB of baked bytes, one mesh, one clock — on demand.
 *
 * `assertTextureLimits` runs here rather than inside the streamer, and against this renderer's real
 * context, because the failure it catches — a GPU whose `MAX_TEXTURE_SIZE` is under 2048 or which
 * offers fewer fragment texture units than the shader binds — should be a sentence before a visitor
 * has waited for 8.7 MB, not a black tower with a console warning afterwards.
 */
assertTextureLimits(renderer, towerData);
const bakeMb = (
  Object.values(towerData.textures).reduce((n, t) => n + t.bytes, 0) / 1048576
).toFixed(1);
/**
 * What each atlas tier weighs, from the bake's own byte tables.
 *
 * The HUD quotes these as the price of the stills half, next to the bake's MB as the price of the
 * live half, so the A/B is labelled with two numbers that came out of the two bakes rather than out
 * of this file. A manifest from before the two-tier atlas has no tables and reports 0.0 MB, which is
 * the honest answer to "how big is it" when nothing published it.
 */
const atlasMb = ((manifest.pageBytes ?? []).reduce((a, b) => a + b, 0) / 1048576).toFixed(1);
const previewKb = ((manifest.previewBytes ?? []).reduce((a, b) => a + b, 0) / 1024).toFixed(0);

/** Which renderer is on screen. `stills` is what opens; `live` animates. */
let mode: "live" | "stills" = "stills";

/**
 * The live half, built the first time it is asked for and kept afterwards.
 *
 * The five fetches are one `Promise.all` inside `loadTowerTextures`, so the wait is the slowest file
 * rather than their sum. Everything downstream reads `parcels` through a null check for exactly this
 * reason: a page that opens on the stills has no parcel mesh, and the alternative — constructing one
 * against empty textures so the type could stay non-null — is a degenerate mesh in the scene and a
 * class of bug that only ever shows up as wrong pixels.
 *
 * `layout` and `applyPresentation` run again at the end, and not as a refresh: an `InstancedMesh`
 * starts with an all-zero `instanceMatrix`, so until they do, this collection is one degenerate point
 * at the origin holding none of the floor slider, highlight or depth state it arrived into.
 */
let parcels: Parcels | null = null;
let baking: Promise<void> | null = null;
function streamBake(): Promise<void> {
  if (baking !== null) return baking;
  baking = (async () => {
    const textures = await loadTowerTextures(".", towerData);
    // Instance to token id: ascending, because ids ascend with height through the census and the
    // layout is arithmetic on the id. `Parcels` keeps this map as `parcels.ids` and picking reads it
    // back, so the two directions can never drift apart.
    const ids = new Int32Array(SUPPLY);
    for (let i = 0; i < SUPPLY; i++) ids[i] = i;
    const built = new Parcels({ data: towerData, textures, ids });
    built.setDetail(detail);
    built.mesh.visible = mode === "live";
    towerGroup.add(built.mesh);
    parcels = built;
    layout();
    applyPresentation();
  })();
  return baking;
}

/**
 * Where the LOD crossover sits, as a multiplier on the shader's Nyquist limit.
 *
 * 1.0 is the honest number: point-sampling a 32-cell grid is defensible while one pixel covers at
 * most one cell, and the baked block means take over beyond that. The reset view puts a parcel at
 * roughly nine pixels — 3.6 cells per pixel — so at 1.0 the tower the live toggle first shows is
 * drawn from block means and does not animate at all, which is correct and looks like a bug.
 *
 * So the default is 2.0. It holds the frame path out to where the whole tower still animates at the
 * reset view, at a cost that is measured rather than argued about: `check.ts` prints the frame
 * rate at 1, 2 and 4 on the same machine, and the slider goes from 0.08 (block means even in a
 * close-up) to 6.
 */
const DEFAULT_DETAIL = 2;
let detail = DEFAULT_DETAIL;

/** One atlas page: 256 consecutive ids, one texture, one draw call. */
interface Page {
  /** `index * PER_PAGE` is the page's first token id — the instance-to-id map, as arithmetic. */
  readonly index: number;
  readonly mesh: InstancedMesh;
  /** Per-instance brightness the highlight filter writes: 1 shown, low dimmed. */
  readonly dim: InstancedBufferAttribute;
  /** Which tier's texture is bound right now, so a late preview cannot undo a full page. */
  tier: "preview" | "full";
}

const pages: Page[] = [];
/**
 * One page's material.
 *
 * A raw `ShaderMaterial` rather than `MeshBasicMaterial` + `onBeforeCompile`, because the whole
 * fragment stage here is one texture fetch and a dim multiply — going through the lambert/basic
 * chunk chain to patch a UV would be more code and more version coupling. `USE_INSTANCING` is
 * defined by three itself for an `InstancedMesh`, which is what makes `instanceMatrix` available.
 *
 * The vertex stage does three jobs beyond the transform. It offsets the UV into the instance's
 * tile. It decides visibility from `aLevel` against `uShown`/`uSolo`, because a page is not a
 * floor and so the floor sliders cannot be `mesh.visible` any more: a hidden instance collapses
 * to `vec4(2, 2, 2, 1)`, which is outside the clip volume in x and y, so the whole primitive is
 * culled before it costs a fragment. And it multiplies the highlight dim by a depth ramp over the
 * shown part of the stack, so the tower reads as depth rather than as one flat pile of tiles.
 */
function pageMaterial(map: Texture): ShaderMaterial {
  return new ShaderMaterial({
    uniforms: {
      uMap: { value: map },
      uScale: { value: 1 / TILE_GRID },
      uShown: { value: shown },
      uSolo: { value: solo },
    },
    side: DoubleSide,
    vertexShader: `
      attribute vec2 aTile;
      attribute float aDim;
      attribute float aLevel;
      varying vec2 vTileUv;
      varying float vDim;
      uniform float uScale;
      uniform float uShown;
      uniform float uSolo;
      void main() {
        vTileUv = uv * uScale + aTile;
        bool isolated = uSolo > 0.5;
        bool visible = isolated ? abs(aLevel - (uSolo - 1.0)) < 0.5 : aLevel < uShown - 0.5;
        float depth = isolated ? 1.0 : mix(0.55, 1.0, aLevel / max(1.0, uShown - 1.0));
        vDim = aDim * depth;
        vec4 p = vec4(position, 1.0);
        #ifdef USE_INSTANCING
          p = instanceMatrix * p;
        #endif
        gl_Position = visible ? projectionMatrix * modelViewMatrix * p : vec4(2.0, 2.0, 2.0, 1.0);
      }
    `,
    fragmentShader: `
      uniform sampler2D uMap;
      varying vec2 vTileUv;
      varying float vDim;
      void main() {
        vec4 c = texture2D(uMap, vTileUv);
        gl_FragColor = vec4(c.rgb * vDim, 1.0);
      }
    `,
  });
}
/** Rebuild every instance transform from the current spacing, in both renderers. */
function layout(): void {
  // One expression for a parcel's place in the world, applied by whoever is drawing it. The
  // marker, the fly-to and `parcelPosition` read the same arithmetic below.
  parcels?.place((id, _instance, mat) => {
    mat.makeTranslation((OFF_X[id] ?? 0) * pitch, tokenLevel(id) * gap, (OFF_Y[id] ?? 0) * pitch);
  });
  const m = new Matrix4();
  for (const page of pages) {
    const first = page.index * PER_PAGE;
    for (let i = 0; i < PER_PAGE; i++) {
      const id = first + i;
      m.makeTranslation((OFF_X[id] ?? 0) * pitch, tokenLevel(id) * gap, (OFF_Y[id] ?? 0) * pitch);
      page.mesh.setMatrixAt(i, m);
    }
    page.mesh.instanceMatrix.needsUpdate = true;
    // `InstancedMesh.raycast` early-outs against a bounding sphere it computes once and caches.
    // Moving the instances without this leaves picking testing yesterday's volume, which is a
    // silent miss on any page whose parcels have spread past the old radius.
    page.mesh.computeBoundingSphere();
  }
  placeMarker();
}

/** Is this floor drawn under the current floors-shown and isolate-floor settings? */
function levelVisible(level: number): boolean {
  return solo === 0 ? level < shown : level === solo - 1;
}

/**
 * The depth cue, as arithmetic.
 *
 * `pageMaterial`'s vertex stage computes this from `aLevel` against `uShown`; the parcel material
 * has no floor attribute and no floor uniforms, so the same expression lives here and reaches it
 * through `present`. Clamped, which the `mix` in the shader is not — it does not need to be, since
 * a floor above `shown - 1` is collapsed before it can be shaded.
 */
const DEPTH_FLOOR = 0.55;
function depthRamp(level: number): number {
  if (solo !== 0) return 1;
  const t = Math.min(1, Math.max(0, level / Math.max(1, shown - 1)));
  return DEPTH_FLOOR + (1 - DEPTH_FLOOR) * t;
}

/**
 * Push the floor sliders, the highlight and the depth ramp to whichever renderers exist.
 *
 * The two paths take it differently by design. A page reads `uShown`/`uSolo` in its vertex shader
 * and keeps the highlight in its own `aDim` attribute, so it only needs the two uniforms here. The
 * live parcels have neither: `present` walks all 4096 ids and writes one `show` flag and one `dim`
 * per instance — two 16 KB uploads, no draw calls — which is why floor visibility, the highlight
 * and the ramp all multiply out on the CPU in one place instead of three shader branches.
 *
 * Both sides are optional at any given moment: the pages arrive one at a time and the parcels not at
 * all until the live toggle is used, so this is written to be safe to call whenever the state it
 * pushes changes and to reach whatever exists then. `streamBake` and `addPage` both call it once
 * more for the half they just built.
 */
function applyPresentation(): void {
  parcels?.present((id) => {
    const level = tokenLevel(id);
    return { show: levelVisible(level), dim: (matches(id) ? 1 : DIMMED) * depthRamp(level) };
  });
  for (const page of pages) {
    const u = (page.mesh.material as ShaderMaterial).uniforms;
    (u.uShown as { value: number }).value = shown;
    (u.uSolo as { value: number }).value = solo;
  }
}

/**
 * How an atlas texture is sampled, for either tier.
 *
 * No `colorSpace`, deliberately, and this is the one line that used to make the stills wrong. These
 * bytes are the sRGB bytes the SVG assigns to `fill`, the same space the palette texture and every
 * blend in the parcel shader work in, and `pageMaterial` writes them out untouched. Tagging the
 * texture sRGB uploads it as `SRGB8_ALPHA8`, so the hardware decodes to linear on sample and those
 * linear values reach an sRGB canvas as-is — a mid grey lands at 0.21 instead of 0.5, and the stills
 * render dark next to the live parcels they are supposed to match.
 *
 * The atlas is a grid of hard-edged pixel art; a mip chain would bleed neighbouring parcels into each
 * other at distance, so filtering stays nearest and unmipped. The preview tier is the same picture at
 * 8px a tile, so it wants exactly the same treatment — the whole point of an exact box average is
 * that a preview tile is the tile, and nothing here should start guessing between its pixels.
 */
function atlasFilter(map: Texture): void {
  map.generateMipmaps = false;
  map.magFilter = NearestFilter;
  map.minFilter = NearestFilter;
}

function addPage(index: number, map: Texture, tier: Tier): void {
  atlasFilter(map);

  const geom = quad.clone();
  const tiles = new Float32Array(PER_PAGE * 2);
  const dims = new Float32Array(PER_PAGE).fill(1);
  const levels = new Float32Array(PER_PAGE);
  const first = index * PER_PAGE;
  for (let i = 0; i < PER_PAGE; i++) {
    const x = Math.floor(i / TILE_GRID);
    const y = i % TILE_GRID;
    tiles[i * 2] = x / TILE_GRID;
    // Image rows run downward, texture rows run upward.
    tiles[i * 2 + 1] = 1 - (y + 1) / TILE_GRID;
    levels[i] = tokenLevel(first + i);
  }
  const dim = new InstancedBufferAttribute(dims, 1);
  geom.setAttribute("aTile", new InstancedBufferAttribute(tiles, 2));
  geom.setAttribute("aDim", dim);
  geom.setAttribute("aLevel", new InstancedBufferAttribute(levels, 1));

  const mesh = new InstancedMesh(geom, pageMaterial(map), PER_PAGE);
  mesh.frustumCulled = false;
  // A page can finish loading after the toggle has been switched back to the live parcels.
  mesh.visible = mode === "stills";
  towerGroup.add(mesh);
  const page: Page = { index, mesh, dim, tier };
  pages.push(page);
  pages.sort((a, b) => a.index - b.index);
  layout();
  applyPresentation();
  applyFilter(page);
}

/**
 * Swap a page's preview texture for the full-resolution one, in place.
 *
 * A texture swap rather than a new mesh: the instance matrices, the per-instance dim attribute and
 * the highlight state already on this page are all correct, and the UV arithmetic is in tile units,
 * so nothing about the geometry depends on how many pixels a tile has. The visitor sees the floor
 * sharpen without it moving.
 *
 * Two orderings have to be survivable, because the two tiers are independent lane pools and a
 * request can fail: a full page arriving where no preview ever landed is just that page's first
 * texture, and a full page arriving twice — or a stray preview after the upgrade — must not undo it.
 */
function upgradePage(index: number, map: Texture): void {
  const page = pages.find((p) => p.index === index);
  if (page === undefined) {
    addPage(index, map, "full");
    return;
  }
  if (page.tier === "full") {
    map.dispose();
    return;
  }
  atlasFilter(map);
  const slot = (page.mesh.material as ShaderMaterial).uniforms.uMap as { value: Texture };
  const old = slot.value;
  slot.value = map;
  page.tier = "full";
  // The preview's 128px upload is dead the moment the uniform points elsewhere, and sixteen of them
  // is 16 × 128² × 4 bytes of GPU memory nothing will ever sample again.
  old.dispose();
}
// ---------------------------------------------------------------- the marker

/**
 * Where a parcel sits in world space, from its id alone.
 *
 * Deliberately independent of `pages`: a parcel found by id can be framed and marked before its
 * atlas has finished streaming, because the geometry is arithmetic over the census, not a lookup
 * into what happens to be loaded.
 */
function parcelPosition(id: number, out: Vector3): Vector3 {
  return out.set((OFF_X[id] ?? 0) * pitch, tokenLevel(id) * gap, (OFF_Y[id] ?? 0) * pitch);
}

/** A square outline slightly larger than a parcel, lying flat. */
function outlineGeometry(): BufferGeometry {
  const s = 0.58;
  const corners: readonly [number, number][] = [
    [-s, -s],
    [s, -s],
    [s, s],
    [-s, s],
  ];
  const pos: number[] = [];
  for (let i = 0; i < corners.length; i++) {
    const a = corners[i] as [number, number];
    const b = corners[(i + 1) % corners.length] as [number, number];
    pos.push(a[0], 0, a[1], b[0], 0, b[1]);
  }
  const geom = new BufferGeometry();
  geom.setAttribute("position", new BufferAttribute(new Float32Array(pos), 3));
  return geom;
}

// `depthTest: false` on both: the point of the marker is to be findable in a stack of sixteen
// floors, which means it has to draw through the floors above it.
const outline = new LineSegments(
  outlineGeometry(),
  new LineBasicMaterial({ color: 0x8fc6ff, depthTest: false, transparent: true, opacity: 0.95 }),
);
outline.renderOrder = 3;
outline.visible = false;
scene.add(outline);

const beamPos = new Float32Array(6);
const beamGeom = new BufferGeometry();
beamGeom.setAttribute("position", new BufferAttribute(beamPos, 3));
const beam = new LineSegments(
  beamGeom,
  new LineBasicMaterial({ color: 0x35618d, depthTest: false, transparent: true, opacity: 0.6 }),
);
beam.renderOrder = 3;
beam.visible = false;
scene.add(beam);

const markerAt = new Vector3();
/** Put the outline and its ground beam on the selected parcel, or hide both. */
function placeMarker(): void {
  if (selected < 0) {
    outline.visible = false;
    beam.visible = false;
    return;
  }
  parcelPosition(selected, markerAt);
  outline.position.set(markerAt.x, markerAt.y + 0.03, markerAt.z);
  outline.visible = true;
  beamPos[0] = markerAt.x;
  beamPos[1] = 0;
  beamPos[2] = markerAt.z;
  beamPos[3] = markerAt.x;
  beamPos[4] = markerAt.y;
  beamPos[5] = markerAt.z;
  (beamGeom.getAttribute("position") as BufferAttribute).needsUpdate = true;
  beam.visible = markerAt.y > 0.1;
}

// ---------------------------------------------------------------- info panel

const info = el<HTMLDivElement>("info");
const infoId = el<HTMLDivElement>("infoId");
const infoTable = el<HTMLTableElement>("infoTable");
const infoLink = el<HTMLAnchorElement>("infoLink");
const swatch = el<HTMLDivElement>("swatch");

function name(column: keyof Manifest["names"], code: number): string {
  const list = manifest.names[column];
  return list === undefined ? String(code) : (list[code] ?? String(code));
}

function showToken(id: number): void {
  const c = manifest.columns;
  const level = tokenLevel(id);
  const side = levelSide(level);
  const rows: [string, string][] = [
    ["parcel", `floor ${level + 1} &middot; ${tokenX(id)},${tokenY(id)}`],
    // The census, per floor, where a visitor is already looking at one floor's parcel. This is
    // the number that differs from floor to floor and makes the tower the shape it is.
    ["this floor", `${side}&times;${side} &middot; ${side * side} parcels`],
    ["archetype", name("archetype", c.archetype[id] ?? 0)],
    ["palette", name("palette", c.palette[id] ?? 0)],
    ["glyphs", name("glyphSet", c.glyphSet[id] ?? 0)],
    ["biome", name("biome", c.biome[id] ?? 0)],
    ["glitch", name("glitch", c.glitch[id] ?? 0)],
    ["hot frames", `${c.hot[id] ?? 0} of 12`],
  ];
  infoId.textContent = `#${id}`;
  infoTable.innerHTML = rows.map(([k, v]) => `<tr><td>${k}</td><td>${v}</td></tr>`).join("");
  swatch.style.background = c.bg[id] ?? "#000";
  infoLink.href = `${location.protocol}//${location.hostname}:${PREVIEW_PORT}/t/${id}`;
  info.style.display = "block";
  selected = id;
  placeMarker();
}

function clearSelection(): void {
  selected = -1;
  info.style.display = "none";
  placeMarker();
}
// ---------------------------------------------------------------- highlight filter

/** How dark a parcel goes when it does not match the highlight. */
const DIMMED = 0.07;

let filterColumn: FilterColumn | null = null;
let filterValue = 0;

function matches(id: number): boolean {
  if (filterColumn === null) return true;
  return (manifest.columns[filterColumn][id] ?? -1) === filterValue;
}

/** Write the per-instance dim for one page, or for all of them. */
function applyFilter(only?: Page): void {
  for (const page of only === undefined ? pages : [only]) {
    const values = page.dim.array as Float32Array;
    const first = page.index * PER_PAGE;
    for (let i = 0; i < PER_PAGE; i++) values[i] = matches(first + i) ? 1 : DIMMED;
    page.dim.needsUpdate = true;
  }
}

/**
 * How many of the 4096 match, counted over the manifest rather than over the loaded pages, so
 * the tally is the collection's answer and does not creep upward while atlases stream in.
 */
function filterTally(): number {
  if (filterColumn === null) return SUPPLY;
  const column = manifest.columns[filterColumn];
  let n = 0;
  for (let id = 0; id < SUPPLY; id++) if ((column[id] ?? -1) === filterValue) n++;
  return n;
}

// ---------------------------------------------------------------- controls

const floorsSlider = el<HTMLInputElement>("floors");
const soloSlider = el<HTMLInputElement>("solo");
const gapSlider = el<HTMLInputElement>("gap");
const pitchSlider = el<HTMLInputElement>("pitch");
const detailSlider = el<HTMLInputElement>("detail");
const liveToggle = el<HTMLInputElement>("live");
const traitSelect = el<HTMLSelectElement>("trait");
const traitValueRow = el<HTMLDivElement>("traitValueRow");
const traitValueSelect = el<HTMLSelectElement>("traitValue");
const traitCount = el<HTMLElement>("traitCount");

/**
 * Every readout in the panel, from the state it is reading.
 *
 * One function rather than a listener per control, because half of these numbers answer to more than
 * one input — the bars follow `shown` *and* `solo`, the detail row follows the renderer as well as
 * its own slider — and a panel that is rebuilt in one pass cannot show two of them disagreeing.
 *
 * `--fill` is the filled half of each fader. Chromium has no `::-webkit-range-progress`, so the track
 * is a gradient whose stop this moves; Firefox uses its own `::-moz-range-progress` and ignores it.
 */
function syncLabels(): void {
  el("floorsVal").innerHTML = solo === 0 ? `1&ndash;${shown}` : "—";
  const side = solo === 0 ? 0 : levelSide(solo - 1);
  el("soloVal").innerHTML = solo === 0 ? "off" : `floor ${solo} &middot; ${side}&times;${side}`;
  el("gapVal").textContent = gap.toFixed(1);
  el("pitchVal").textContent = pitch.toFixed(2);
  // The slider means nothing until there is a frame path to point at, and `setMode` disables it
  // whenever there is not — so this is only reached with a live mesh in the scene. The `?.` is the
  // type system's copy of that fact rather than a second policy.
  el("detailVal").innerHTML =
    mode === "live" && parcels !== null ? `${detail.toFixed(2)}&times;` : "&mdash;";
  el("detailRow").classList.toggle("dead", detailSlider.disabled);
  el("costStills").classList.toggle("now", mode === "stills");
  el("costLive").classList.toggle("now", mode === "live");
  for (const slider of [floorsSlider, soloSlider, gapSlider, pitchSlider, detailSlider]) {
    const min = Number(slider.min);
    const span = Number(slider.max) - min;
    const at = span > 0 ? ((Number(slider.value) - min) / span) * 100 : 0;
    slider.style.setProperty("--fill", `${at.toFixed(1)}%`);
  }
  for (let level = 0; level < bars.length; level++) {
    const bar = bars[level];
    if (bar === undefined) continue;
    bar.classList.toggle("on", solo === level + 1);
    bar.classList.toggle("off", solo === 0 ? level >= shown : solo !== level + 1);
  }
  traitCount.textContent = `${filterTally()} / ${SUPPLY}`;
}
function isolate(level: number): void {
  solo = level;
  soloSlider.value = String(level);
  applyPresentation();
  syncLabels();
}

floorsSlider.max = String(LEVELS);
floorsSlider.value = String(LEVELS);
soloSlider.max = String(LEVELS);

floorsSlider.addEventListener("input", () => {
  shown = Number(floorsSlider.value);
  applyPresentation();
  syncLabels();
});
soloSlider.addEventListener("input", () => {
  solo = Number(soloSlider.value);
  applyPresentation();
  syncLabels();
});
gapSlider.addEventListener("input", () => {
  gap = Number(gapSlider.value) / 10;
  layout();
  syncLabels();
});
pitchSlider.addEventListener("input", () => {
  pitch = Number(pitchSlider.value) / 100;
  layout();
  syncLabels();
});
detailSlider.addEventListener("input", () => {
  detail = Number(detailSlider.value) / 100;
  parcels?.setDetail(detail);
  syncLabels();
});
liveToggle.addEventListener("change", () => {
  setMode(liveToggle.checked ? "live" : "stills");
});

traitSelect.addEventListener("change", () => {
  const chosen = traitSelect.value;
  const column = (FILTER_COLUMNS as readonly string[]).includes(chosen) ? (chosen as FilterColumn) : null;
  filterColumn = column;
  filterValue = 0;
  traitValueRow.hidden = column === null;
  traitValueSelect.innerHTML =
    column === null
      ? ""
      : (manifest.names[column] ?? [])
          .map((label, i) => `<option value="${i}">${label}</option>`)
          .join("");
  applyFilter();
  applyPresentation();
  syncLabels();
});
traitValueSelect.addEventListener("change", () => {
  filterValue = Number(traitValueSelect.value);
  applyFilter();
  applyPresentation();
  syncLabels();
});
// ---------------------------------------------------------------- the readouts

/**
 * The spec sheet, written out of the bake instead of typed into the markup.
 *
 * Every row is a claim something else in the repo can be held to: the raster and the loop are what
 * `tower:parity` compares byte for byte, the schedule counts are what the engine emitted, the atlas
 * geometry is what `tools/atlas.ts` laid out, and the salt is what every trait was derived from. So
 * a re-bake at a different supply, frame rate or palette vocabulary moves the panel with it, and a
 * panel that disagrees with the art beside it is a bug rather than stale copy.
 */
function fillSpec(): void {
  const n = (v: number): string => v.toLocaleString("en-US");
  const records = SUPPLY * towerData.frames;
  const hot = manifest.columns.hot.reduce((a, b) => a + b, 0);
  const cells = SUPPLY * towerData.cols * towerData.rows * towerData.frames;
  const vocab = FILTER_COLUMNS.map((column) => manifest.names[column]?.length ?? 0);
  const combos = vocab.reduce((a, b) => a * b, 1);
  const formats = [...new Set(Object.values(towerData.textures).map((t) => t.format))];
  const rows: readonly (readonly [string, string])[] = [
    [
      "raster",
      `<b>${towerData.cols}&times;${towerData.rows}</b> cells &middot; <b>${towerData.frames}</b>`
      + ` frames &middot; ${towerData.loopMs}ms`,
    ],
    ["volume", `<b>${n(cells)}</b> cells &middot; ${n(cells / SUPPLY)}/token`],
    ["glitch", `<b>${n(hot)}</b> of ${n(records)} frames &middot; ${((hot / records) * 100).toFixed(1)}%`],
    ["traits", `<b>${n(combos)}</b> tuples &middot; ${vocab.join("&middot;")}`],
    ["atlas", `<b>${PAGES}</b> pages &middot; ${manifest.atlasPx}&sup2; &middot; ${manifest.tile}px`],
    [
      "stream",
      manifest.previewPx !== undefined && manifest.previewPx > 0
        ? `<b>${previewKb} KB</b> at ${manifest.previewTile ?? 0}px &rarr; ${atlasMb} MB lossless`
        : `<b>${atlasMb} MB</b> &middot; one tier`,
    ],
    ["bake", `<b>${bakeMb} MB</b> &middot; ${formats.join(" ")}`],
    ["salt", `${manifest.salt.slice(0, 10)}&hellip;${manifest.salt.slice(-6)}`],
  ];
  el("spec").innerHTML = rows.map(([k, v]) => `<dt>${k}</dt><dd>${v}</dd>`).join("");
}

/**
 * The census, twice over: the silhouette it produces and the numbers behind it.
 *
 * The bars are mapped to parcels *a side*, not to the parcel count, because a side is the footprint
 * the tower actually shows at that height — the count is that squared, so a linear map would put
 * floor 1 half a pixel tall beside floor 11 and the shape would be unreadable. Each bar isolates its
 * floor, the same click the level axis takes, because the bar is what a visitor is already pointing
 * at when they ask about a floor.
 */
const bars: HTMLButtonElement[] = [];
function buildCensus(): void {
  const host = el("bars");
  for (let level = 0; level < LEVELS; level++) {
    const side = levelSide(level);
    const bar = document.createElement("button");
    bar.type = "button";
    bar.className = "bar";
    bar.style.height = `${((side / SPAN) * 100).toFixed(1)}%`;
    const label = `floor ${level + 1} — ${side}×${side}, ${side * side} parcels`;
    bar.title = label;
    bar.setAttribute("aria-label", label);
    bar.addEventListener("click", () => {
      isolate(solo === level + 1 ? 0 : level + 1);
    });
    host.appendChild(bar);
    bars.push(bar);
  }
  el("census").textContent = SIDES.join("  ");
}

// The three lines that say what this is, from the manifest: the supply and the range of floor sizes,
// the census total, and the byte cost of each half of the A/B. The sub-line is the one place the
// tower says out loud that its floors are not all the same size.
el("sub").innerHTML =
  `${SUPPLY} parcels &middot; ${LEVELS} floors &middot; `
  + `${SIDES[0]}&times;${SIDES[0]} &rarr; ${SPAN}&times;${SPAN}`;
el("censusSum").textContent = `${SUPPLY} parcels`;
el("costStills").textContent = `${PAGES} pages · ${atlasMb} MB`;
el("costLive").textContent = `${towerData.frames} frames · ${bakeMb} MB`;
fillSpec();
buildCensus();

// ---------------------------------------------------------------- the level axis

const ruler = el<HTMLDivElement>("ruler");

interface Tick {
  readonly level: number;
  readonly node: HTMLDivElement;
}

const ticks: Tick[] = [];
for (let level = LEVELS - 1; level >= 0; level--) {
  const node = document.createElement("div");
  const side = levelSide(level);
  node.className = "tick";
  node.innerHTML = `<b>${String(level + 1).padStart(2, "0")}</b><i></i>`;
  // The census, on hover, floor by floor — the ruler is where a visitor asks "how big is that
  // one" and it is the one place the numbers can live without another panel.
  node.title = `floor ${level + 1} — ${side}×${side}, ${side * side} parcels`;
  node.addEventListener("click", () => {
    isolate(solo === level + 1 ? 0 : level + 1);
  });
  ruler.appendChild(node);
  ticks.push({ level, node });
}

const probe = new Vector3();

/** Slide each floor number to its floor's projected height. Called every frame. */
function updateRuler(): void {
  const h = innerHeight;
  for (const tick of ticks) {
    probe.set(0, tick.level * gap, 0).project(camera);
    const y = (1 - probe.y) * 0.5 * h;
    const onScreen = probe.z < 1 && y > -16 && y < h + 16;
    const listed = solo === 0 ? tick.level < shown : true;
    tick.node.style.display = onScreen && listed ? "block" : "none";
    tick.node.style.transform = `translateY(${y.toFixed(1)}px)`;
    tick.node.classList.toggle("on", solo === tick.level + 1 || (selected >= 0 && tokenLevel(selected) === tick.level));
  }
}
// ---------------------------------------------------------------- find by id

interface Flight {
  readonly camFrom: Vector3;
  readonly camTo: Vector3;
  readonly aimFrom: Vector3;
  readonly aimTo: Vector3;
  readonly t0: number;
  readonly ms: number;
}

let flight: Flight | null = null;

/** Swing the camera onto one parcel. Any touch of the mouse cancels it — see `pointerdown`. */
function flyTo(id: number): void {
  const aim = parcelPosition(id, new Vector3());
  flight = {
    camFrom: camera.position.clone(),
    camTo: aim.clone().add(new Vector3(pitch * 4.5, gap * 1.2 + 4, pitch * 4.5)),
    aimFrom: controls.target.clone(),
    aimTo: aim,
    t0: performance.now(),
    ms: 900,
  };
}

const findInput = el<HTMLInputElement>("findId");
const findGo = el<HTMLButtonElement>("findGo");

/**
 * Select a parcel by id and fly to it.
 *
 * A parcel hidden by the floor sliders cannot be looked at, so finding one re-opens the stack
 * rather than flying the camera to an empty patch of air.
 */
function goTo(raw: string): void {
  const id = Number.parseInt(raw.trim(), 10);
  if (!Number.isInteger(id) || id < 0 || id >= SUPPLY) {
    findInput.classList.add("bad");
    return;
  }
  findInput.classList.remove("bad");
  const level = tokenLevel(id);
  if (solo !== 0 && solo - 1 !== level) isolate(0);
  if (shown <= level) {
    shown = LEVELS;
    floorsSlider.value = String(LEVELS);
    applyPresentation();
    syncLabels();
  }
  showToken(id);
  flyTo(id);
}

findGo.addEventListener("click", () => goTo(findInput.value));
findInput.addEventListener("keydown", (ev: KeyboardEvent) => {
  if (ev.key === "Enter") goTo(findInput.value);
});
// ---------------------------------------------------------------- picking

const raycaster = new Raycaster();
const pointer = new Vector2();
let dragged = false;

/**
 * Which token an instanced hit is, whichever renderer answered it.
 *
 * The live mesh carries its own instance-to-id map — `parcels.ids` is the array the constructor was
 * handed — and a page's map is arithmetic on its index. Reading the map back rather than assuming
 * `instanceId === id` is what lets the tower reorder or thin the instances later without picking
 * quietly reporting the wrong parcel.
 */
function hitToken(object: Object3D, instance: number): number {
  if (parcels !== null && object === parcels.mesh) return parcels.ids[instance] ?? -1;
  const page = pages.find((p) => p.mesh === object);
  return page === undefined ? -1 : page.index * PER_PAGE + instance;
}

canvas.addEventListener("pointerdown", () => {
  dragged = false;
  // The camera is the user's now; a tween still running would fight the orbit.
  flight = null;
});
canvas.addEventListener("pointermove", () => {
  dragged = true;
});
canvas.addEventListener("wheel", () => {
  flight = null;
});
canvas.addEventListener("pointerup", (ev: PointerEvent) => {
  if (dragged || ev.button !== 0) return;
  const rect = canvas.getBoundingClientRect();
  pointer.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
  pointer.y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
  raycaster.setFromCamera(pointer, camera);
  const hits = raycaster.intersectObjects(
    mode === "live" && parcels !== null ? [parcels.mesh] : pages.map((p) => p.mesh),
    false,
  );
  // Hidden floors are collapsed in the vertex shader, not removed from the geometry, so the
  // raycaster still finds them. Sorted near to far already, so the first hit on a floor that is
  // actually drawn is the parcel the visitor clicked; without this a floor hidden by the sliders
  // would answer for the one behind it.
  for (const hit of hits) {
    if (hit.instanceId === undefined) continue;
    const id = hitToken(hit.object, hit.instanceId);
    if (id < 0 || !levelVisible(tokenLevel(id))) continue;
    showToken(id);
    return;
  }
});
addEventListener("keydown", (ev: KeyboardEvent) => {
  const typing = ev.target instanceof HTMLInputElement || ev.target instanceof HTMLSelectElement;
  if (ev.key === "Escape") {
    clearSelection();
    findInput.blur();
    return;
  }
  if (typing) return;
  if (ev.key === "/") {
    ev.preventDefault();
    findInput.focus();
    findInput.select();
  }
  if (ev.key === "r" || ev.key === "R") {
    flight = null;
    resetView();
  }
  // A/B the two renderers without reaching for the HUD, because the only way to judge whether the
  // live shader is drawing the art is to flip between it and the stills on the same framing.
  if (ev.key === "a" || ev.key === "A") {
    liveToggle.checked = !liveToggle.checked;
    setMode(liveToggle.checked ? "live" : "stills");
  }
});

function resize(): void {
  const w = innerWidth;
  const h = innerHeight;
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
  renderer.setSize(w, h, false);
  camera.aspect = w / h;
  camera.updateProjectionMatrix();
}
addEventListener("resize", resize);
resize();
resetView();

// ---------------------------------------------------------------- the two renderers

/** The line the status bar settles on: the census, the salt, and which renderer is drawing. */
function settled(): string {
  const census = `${SUPPLY} parcels · ${LEVELS} floors · ${SIDES[0]}×${SIDES[0]} to ${SPAN}×${SPAN}`;
  const salt = `salt ${manifest.salt.slice(0, 10)}…`;
  const how =
    mode === "live"
      ? `live · ${towerData.frames} frames in ${towerData.loopMs}ms · ${bakeMb} MB baked`
      : `atlas stills · frame 0 · ${PAGES} pages`;
  return `${census} · ${salt} · ${how}`;
}

/** Say what is on screen and let the bar sink back out of the way. */
function settle(): void {
  statusText.textContent = settled();
  statusBar.style.opacity = "1";
  setTimeout(() => {
    if (statusText.textContent === settled()) statusBar.style.opacity = "0.45";
  }, 1200);
}

let streaming = false;

// ---------------------------------------------------------------- the atlas, in two tiers

/** Which resolution of a page: the small preview, or the full-size atlas that replaces it. */
type Tier = "preview" | "full";

const ATLAS_EXT = manifest.atlasFormat ?? "png";
const PREVIEW_PX = manifest.previewPx ?? 0;
const PREVIEW_TILE = manifest.previewTile ?? 0;
const TIERS = PREVIEW_PX > 0 ? 2 : 1;

/**
 * What each page costs in each tier, and whether those numbers are real.
 *
 * `tools/atlas.ts` publishes both byte tables, and that is what lets the bar show a fraction of the
 * download instead of a count of files of very unequal size: one preview is 24 KB where one page is
 * 347 KB, so "3 of 16" moves a page-counting bar by wildly different amounts depending on which
 * three. A manifest baked before the two-tier atlas carries no tables — then every file weighs one
 * and the panel counts pages instead, which is coarser but still true.
 */
const FULL_COST = Array.from({ length: PAGES }, (_, i) => manifest.pageBytes?.[i] ?? 0);
const LOW_COST = Array.from({ length: PAGES }, (_, i) => manifest.previewBytes?.[i] ?? 0);
const IN_BYTES = FULL_COST.every((n) => n > 0) && (PREVIEW_PX === 0 || LOW_COST.every((n) => n > 0));
const atlasTotal = IN_BYTES
  ? FULL_COST.reduce((a, b) => a + b, 0) + (PREVIEW_PX > 0 ? LOW_COST.reduce((a, b) => a + b, 0) : 0)
  : PAGES * TIERS;
const cost = (tier: Tier, index: number): number =>
  IN_BYTES ? ((tier === "preview" ? LOW_COST : FULL_COST)[index] ?? 0) : 1;

/** One cell per page in the loading panel, ground first — the census silhouette, filling up. */
const bootCells = Array.from({ length: PAGES }, () => {
  const cell = document.createElement("i");
  bootPages.append(cell);
  return cell;
});

const mbOf = (bytes: number): string => (bytes / 1_048_576).toFixed(1);
let atlasDone = 0;
const landed: Record<Tier, number> = { preview: 0, full: 0 };
const failed: number[] = [];

/**
 * Report one page's arrival — the panel while it is still up, the status bar after it has gone.
 *
 * Both tiers come through here, so the percentage is over the whole download rather than resetting
 * when the second tier starts. That is the honest denominator: a visitor who sees 6% has 6% of the
 * bytes, even though the tower they are looking at is already complete at the preview resolution.
 */
function atlasProgress(tier: Tier, index: number): void {
  atlasDone += cost(tier, index);
  landed[tier]++;
  bootCells[index]?.classList.add(tier === "preview" ? "lo" : "hi");
  const pct = Math.min(100, Math.round((100 * atlasDone) / atlasTotal));
  boot.classList.add("counting");
  bootFill.style.width = `${pct}%`;
  bootPct.textContent = `${pct}%`;
  bootBytes.textContent = IN_BYTES
    ? `${mbOf(atlasDone)} of ${mbOf(atlasTotal)} MB`
    : `${landed.preview + landed.full} of ${PAGES * TIERS} pages`;
  const what = tier === "preview" ? `${PREVIEW_TILE}px previews` : `${manifest.tile}px pages`;
  bootSub.textContent = `${what} · ${landed[tier]} of ${PAGES}`;
  if (mode === "stills" && tier === "full" && landed.full < PAGES) {
    statusText.textContent = `sharpening ${landed.full} of ${PAGES} pages…`;
  }
  // The panel can leave in the middle of the preview tier — `BOOT_CEILING_MS` — and when it does the
  // status bar is the only thing left counting, so it picks the count up from wherever the panel left it.
  if (mode === "stills" && tier === "preview" && bootGone() && landed.preview < PAGES) {
    statusText.textContent = `atlas ${landed.preview} of ${PAGES} previews…`;
  }
}

/** A page that would not load, said once per page and left on screen. */
function atlasFailed(tier: Tier, index: number, err: unknown): void {
  failed.push(index);
  const file = `page-${String(index).padStart(2, "0")}${tier === "preview" ? "-lo.webp" : `.${ATLAS_EXT}`}`;
  const why = err instanceof Error ? err.message : String(err);
  boot.classList.add("bad");
  bootSub.textContent = `${file} did not load (${failed.length} of ${PAGES * TIERS}) — ${why}`;
  statusText.textContent = `${file} failed to load — re-run tools/atlas.ts`;
  statusBar.style.opacity = "1";
}

/**
 * Fetch one tier's pages in floor order, `lanes` of them at a time.
 *
 * Width and order pull in opposite directions and both matter. Sixteen at once is what the first
 * version did: HTTP/2 puts them all on one connection, every page therefore finishes near the end of
 * the download, and a visitor on a slow line watched an empty sky for two minutes instead of a tower
 * growing. One at a time leaves a fast line idle between round trips. Four lanes in page order — and
 * ids ascend with height, so page order is floor order — keeps a fast pipe full while still landing
 * the ground floor first.
 *
 * A page that fails is reported and skipped rather than taking the tier down with it: fifteen floors
 * and a gap is a better answer than a black screen, and the panel says which page is missing.
 */
async function streamTier(tier: Tier, lanes: number, onPage: (index: number, map: Texture) => void): Promise<void> {
  const loader = new TextureLoader();
  const suffix = tier === "preview" ? "-lo.webp" : `.${ATLAS_EXT}`;
  let next = 0;
  const lane = async (): Promise<void> => {
    while (next < PAGES) {
      const index = next++;
      try {
        const map = await loader.loadAsync(`./page-${String(index).padStart(2, "0")}${suffix}`);
        onPage(index, map);
        atlasProgress(tier, index);
      } catch (err: unknown) {
        atlasFailed(tier, index, err);
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(lanes, PAGES) }, lane));
}

/**
 * Ask for the atlas, once, smallest tier first. This is the opening view, so it runs at load.
 *
 * The two tiers are the whole point. 386 KB of 8px-a-tile previews is a complete, legible tower in
 * about three seconds on a 130 KB/s line; the 5.7 MB of full pages then replaces each one in place,
 * while the visitor is already orbiting something. Before this, the only tier was the big one and the
 * sky stayed empty for the entire two-minute download.
 *
 * The loading panel goes away when the tower is complete — after the previews, not after the whole
 * download — and the status bar carries the sharpening pass from there. `streaming` is what makes a
 * second ask (the renderer toggle flipped twice before the last page landed) a no-op rather than
 * another thirty-two requests and sixteen duplicate meshes.
 */
function streamStills(): void {
  if (streaming) return;
  streaming = true;
  void (async () => {
    if (PREVIEW_PX > 0) {
      bootSub.textContent = `${PREVIEW_TILE}px previews · 0 of ${PAGES}`;
      await streamTier("preview", 4, (index, map) => {
        addPage(index, map, "preview");
      });
      // The tower is on screen and whole; the rest is resolution, and it arrives behind a status line
      // rather than behind a panel over the thing it is improving.
      if (failed.length === 0) bootDone();
      if (mode === "stills") statusText.textContent = `sharpening 0 of ${PAGES} pages…`;
    }
    await streamTier("full", 4, upgradePage);
    if (failed.length === 0) bootDone();
    if (mode === "stills") settle();
  })();
}

/**
 * Swap which renderer is on screen, and stream the half that has not been fetched yet.
 *
 * Either direction can be the first one. The stills are asked for at load and the bake only when
 * `live` is first chosen, so one side or the other can be mid-flight, or absent, when the toggle
 * moves. What is true afterwards either way: `mode` names the half a visitor asked for, the other
 * half is hidden rather than unloaded, and the bar says which is drawing or what is being waited for.
 *
 * The detail slider belongs to the frame path, so it is disabled outside it — including while the
 * bake is in flight, because a control that silently does nothing is worse than one that says it
 * cannot yet.
 */
function setMode(next: "live" | "stills"): void {
  mode = next;
  if (parcels !== null) parcels.mesh.visible = next === "live";
  for (const page of pages) page.mesh.visible = next === "stills";
  detailSlider.disabled = next !== "live" || parcels === null;
  if (next === "live" && parcels === null) {
    statusText.textContent = `loading ${bakeMb} MB of parcel data…`;
    statusBar.style.opacity = "1";
    void streamBake().then(
      () => {
        detailSlider.disabled = mode !== "live";
        if (mode === "live") settle();
        syncLabels();
      },
      (err: unknown) => {
        // `loadTowerTextures` fails with a sentence naming both numbers; putting it in the bar is
        // the difference between "the tower went black" and a bug report.
        statusText.textContent = `the parcel bake failed — ${err instanceof Error ? err.message : String(err)}`;
        statusBar.style.opacity = "1";
      },
    );
  } else if (next === "stills" && (pages.length < PAGES || landed.full < PAGES)) {
    // Three things "not finished" can mean now, and they read differently to a visitor: nothing asked
    // for yet, the previews still arriving, or a complete tower being sharpened page by page.
    statusText.textContent = !streaming
      ? `fetching ${PAGES} atlases…`
      : landed.preview >= PAGES || PREVIEW_PX === 0
        ? `sharpening ${landed.full} of ${PAGES} pages…`
        : `atlas ${pages.length} of ${PAGES}…`;
    statusBar.style.opacity = "1";
    streamStills();
  } else {
    settle();
  }
  syncLabels();
}

detailSlider.value = String(Math.round(DEFAULT_DETAIL * 100));
// The frame path does not exist yet, and the HUD has to agree with `mode` before a visitor touches
// it — set here as well as in the markup so a reload cannot leave the checkbox and the renderer
// disagreeing about which half is on screen.
detailSlider.disabled = true;
liveToggle.checked = false;
syncLabels();
// The opening view. Each half lays itself out and applies the current presentation as it is built —
// `addPage` per page, `streamBake` once — because an `InstancedMesh` starts with an all-zero
// `instanceMatrix` rather than identity, so an un-laid-out collection is not stale, it is a
// degenerate point at the origin.
statusText.textContent = PREVIEW_PX > 0
  ? `fetching ${PAGES} previews, then ${PAGES} pages…`
  : `fetching ${PAGES} atlases…`;
statusBar.style.opacity = "1";
streamStills();

let frames = 0;
let fpsAt = performance.now();
/** The frame index currently in the uniform, so a re-upload only happens when the loop ticks. */
let liveFrame = -1;

function frame(): void {
  const now = performance.now();
  // The loop. `frameOf` walks the same twelve boundaries `frameStartMs` gives the payload's own
  // player, so the parcel in the tower and the parcel in a browser tab are on the same frame at
  // the same millisecond — twelve steps of a second, not a tween.
  if (mode === "live" && parcels !== null) {
    const f = parcels.frameOf(now);
    if (f !== liveFrame) {
      parcels.setFrame(f);
      liveFrame = f;
    }
  }
  if (flight !== null) {
    const t = Math.min(1, (now - flight.t0) / flight.ms);
    const e = t * t * (3 - 2 * t);
    camera.position.lerpVectors(flight.camFrom, flight.camTo, e);
    controls.target.lerpVectors(flight.aimFrom, flight.aimTo, e);
    if (t >= 1) flight = null;
  }
  controls.update();
  renderer.render(scene, camera);
  updateRuler();
  frames++;
  if (now - fpsAt >= 500) {
    fpsOut.textContent = `${Math.round((frames * 1000) / (now - fpsAt))} fps`;
    frames = 0;
    fpsAt = now;
  }
  requestAnimationFrame(frame);
}
frame();
